
import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Subject, takeUntil } from 'rxjs';
import { FilterService } from './services/filter.service';

export interface FilterFieldConfig {
    name: string;
    label: string;
    type: 'select' | 'input' | 'date';
    multiple?: boolean;
    inputType?: string; // e.g. 'text', 'number', 'date'
    options?: { label: string; value: any }[];
    placeholder?: string;
    initialValue?: any;
  }

@Component({
  selector: 'app-filter',
  templateUrl: './filter.component.html',
  styleUrls: ['./filter.component.scss'],
  imports: [ReactiveFormsModule, FormsModule, MatIconModule, MatTooltipModule, MatCardModule, MatSelectModule, MatFormFieldModule, MatInputModule, MatButtonModule, MatCheckboxModule, MatDatepickerModule]
})
export class FilterComponent implements OnInit, OnChanges, OnDestroy {
  private destroy$ = new Subject<void>();

  @Input() filterScope = 'default-filter-scope';
  @Input() filterConfig: FilterFieldConfig[] = [];
  @Output() filterChange = new EventEmitter<any>();

  filterForm!: FormGroup;

  constructor(
    private fb: FormBuilder,
    private filterService: FilterService
  ) {}

  ngOnInit(): void {
    // If you expect the config to be stable at ngOnInit, build once here
    // But if the config can change after init, handle it in ngOnChanges below
    this.buildForm();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // If filterConfig changes dynamically (e.g. after async data load),
    // rebuild the form with new fields and reapply saved/initial values
    if (changes['filterConfig'] && !changes['filterConfig'].firstChange) {
      this.buildForm();
    }
  }

  private buildForm(): void {
    const savedFilterValues = this.filterService.getFilters(this.filterScope);
    const group: { [key: string]: FormControl } = {};

    this.filterConfig.forEach(filter => {
      const savedValue = savedFilterValues[filter.name];
      // If there's a saved value, use it; else, use filter.initialValue (if any)
      group[filter.name] = new FormControl(
        savedValue !== null && savedValue !== undefined ? savedValue : (filter.initialValue ?? '')
      );
    });

    this.filterForm = this.fb.group(group);

    // Immediately emit the form's initial state to the parent
    this.filterChange.emit(this.filterForm.value);

    // Listen for changes and update the FilterStateService + emit up
    this.filterForm.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(values => {
      this.filterService.setFilters(this.filterScope, values);
      this.filterChange.emit(values);
    });
  }

  resetFilters(): void {
    // Reset each control to its initial value, rather than always ''
    const resetValues: any = {};
    this.filterConfig.forEach(filter => {
      resetValues[filter.name] = filter.initialValue ?? '';
    });

    this.filterForm.reset(resetValues);
    // Also reset the FilterStateService so next time the user returns, 
    // it doesn't re-populate with old values
    this.filterService.resetFilters(this.filterScope);

    // Emitting after reset so parent can respond (like re-calling an API)
    this.filterChange.emit(this.filterForm.value);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}


